import { useEffect, useState } from "react";
import { domain, token } from "../../components/Security";
import axios from "../.../../../api/apiClient";
import { Select } from "../Select"; // Assuming you have a general Select component

export function PayTypeSelect({
  // Select component props
  label,
  isRequired = false,
  isDisabled = false,
  isEditMode = false,
  value,
  onChange,
  setValue,
  placeholder = "Choose pay type",
  className = "",
  parentClassName = "",
  error = false,
  errorMessage = "",
  isLoading = false,
  allowSelectAll = false,
  
  // Legacy props for backward compatibility
  payTypeId,        // legacy selected value
  setPayTypeId,     // legacy setter
  isEditable,       // legacy editability
  
  // Additional props
  includeInactive = false,
  
  ...props
}) {
  const [payTypes, setPayTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  
  // Legacy mode detection
  const isLegacyMode = payTypeId !== undefined || setPayTypeId !== undefined;
  
  // Determine value for Select component
  const selectValue = isLegacyMode ? payTypeId?.toString() || "" : value || "";
  
  // Determine disabled state
  const disabled = isEditMode || isDisabled || loading || isLoading || (isLegacyMode && isEditable === false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const endpoint = includeInactive 
          ? `/api/PayType` 
          : `/api/PayType/active`;
        
        const response = await axios.get(domain + endpoint, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        // Sort by description for consistency
        const sortedData = response.data.sort((a, b) => 
          (a.description || "").localeCompare(b.description || "")
        );
        
        setPayTypes(sortedData);
        
        // Transform to options format for Select component
        const transformedOptions = sortedData.map(payType => {
          let label = payType.description || "";
          
          // Add inactive indicator
          if (includeInactive && !payType.isActive) {
            label += " [Inactive]";
          }
          
          return {
            value: payType.id.toString(),
            label: label,
            rawData: payType
          };
        });
        
        setOptions(transformedOptions);
      } catch (err) {
        console.error('Error fetching pay types:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [includeInactive]);

  const handleChange = (e) => {
    const selectedValue = e.target.value;
    
    if (isLegacyMode) {
      // Legacy mode: maintain existing behavior
      if (setPayTypeId) {
        setPayTypeId(selectedValue);
      }
    } else {
      // New mode: handle both onChange and setValue patterns
      if (onChange) {
        onChange(e);
      } else if (setValue) {
        setValue(selectedValue);
      }
    }
  };

  // Handle loading state
  if (loading) {
    return (
      <Select
        label={label}
        isRequired={isRequired}
        isDisabled={true}
        isEditMode={isEditMode}
        value={selectValue}
        onChange={handleChange}
        options={[]}
        placeholder="Loading pay types..."
        className={className}
        parentClassName={parentClassName}
        error={error}
        errorMessage={errorMessage}
        allowSelectAll={allowSelectAll}
        {...props}
      />
    );
  }

  return (
    <Select
      label={label}
      isRequired={isRequired}
      isDisabled={disabled}
      isEditMode={isEditMode}
      value={selectValue}
      onChange={handleChange}
      options={options}
      placeholder={placeholder}
      className={className}
      parentClassName={parentClassName}
      error={error}
      errorMessage={errorMessage}
      allowSelectAll={allowSelectAll}
      {...props}
    />
  );
}